<template>
  <div ref="rootEl" class="tw:relative">
    <label :for="id" :class="{ 'tw:mb-0': !label }" class="tw:w-full tw:mb-0">
      <p class="tw:font-bold">{{ label }}</p>
      <div class="tw:relative">
        <input
          :id="id"
          :value="searchValue"
          :type="type"
          v-bind="$attrs"
          class="tw:appearance-none tw:border tw:w-full tw:focus:outline-0 tw:bg-white tw:text-gray-800 tw:focus:border-blue-800 tw:py-2 tw:px-3 tw:rounded-lg"
          :class="`${sizeClass} tw:border-gray-500`"
          @input="triggerUpdate"
          @focus="showResultsOnFocus"
        />
        <div
          v-if="showExampleText"
          class="tw:text-sm tw:bg-transparent tw:absolute tw:text-gray-300 tw:right-1 tw:top-[1px] tw:bottom-1 tw:left-[18px] tw:pr-4 tw:py-2 tw:rounded-lg"
          type="text"
        >
          {{ tempCurrentValue }}
        </div>
      </div>
    </label>
    <small v-if="error" class="tw:text-red-700">{{ error }}</small>

    <BaseCard v-if="isOpen" no-padding class="tw:max-h-[250px] tw:overflow-auto tw:absolute tw:right-0 tw:left-0">
      <ul v-if="isLoading">
        <li
          class="tw:p-2 tw:hover:bg-gray-100 tw:border-b tw:border-gray-100 tw:text-gray-300 tw:flex tw:justify-center tw:py-4"
        >
          <SmallDotLoader color="#ccc" />
        </li>
      </ul>
      <ul v-if="suggestedOptions.length > 0">
        <li
          v-for="(item, index) in suggestedOptions"
          :key="index"
          class="tw:z-10 tw:relative tw:bg-white tw:p-2 tw:hover:cursor-pointer tw:hover:bg-gray-100 tw:border-b tw:border-gray-100"
          @click.stop="selectNewValue(item)"
          @keydown.enter="selectNewValue(item)"
        >
          {{ item.label }}
        </li>
      </ul>

      <ul v-else>
        <li class="tw:p-2 tw:hover:bg-gray-100 tw:border-b tw:border-gray-100 tw:text-gray-300">
          {{ $t('no_results') }}
        </li>
      </ul>
    </BaseCard>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'

import BaseCard from '@/components/dashboard/BaseCard.vue'
import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import PropertyDetailsService from '@/services/PropertyDetailsService'

// Props
const props = defineProps({
  id: {
    type: String,
    default: 'autocomplete',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  ariaLabel: {
    type: String,
    required: true,
  },
  value: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    required: false,
    default: 'text',
  },
  error: {
    type: [Boolean, String],
    default: false,
  },
  size: {
    type: String,
    default: 'medium',
  },
  options: {
    type: Array,
    default: () => [],
  },
  locale: {
    type: String,
    default: 'en-US',
  },
})

let searchTimeout

// Emits
const emit = defineEmits(['onSelect'])

// Template ref for root element
const rootEl = ref(null)

// Reactive data
const isOpen = ref(false)
const suggestedOptions = ref([])
const searchValue = ref(props.value)
const isLoading = ref(false)
const tempCurrentValue = ref(props.value)
const showExampleText = ref(false)

watch(
  () => props.value,
  newValue => {
    searchValue.value = newValue
    tempCurrentValue.value = newValue
  }
)

// Computed
const sizeClass = computed(() => {
  if (props.size === 'medium') return 'tw:px-4 box-size-36 tw:text-sm tw:rounded-lg '
  else return 'tw:px-6 box-size-44 tw:text-base tw:rounded-lg'
})

// Watchers
watch(
  () => props.options,
  (val, oldValue) => {
    if (val.length !== oldValue.length) {
      suggestedOptions.value = val
      isLoading.value = false
    }
  }
)

watch(searchValue, (val, oldValue) => {
  if (oldValue === '') {
    showExampleText.value = false
  }
  if (val === '') {
    showExampleText.value = true
  }
})

// Methods
const handleClickOutside = evt => {
  if (rootEl.value && !rootEl.value.contains(evt.target)) {
    isOpen.value = false
    searchValue.value = props.value
  }
}

const selectNewValue = item => {
  emit('onSelect', item)
  isOpen.value = false
  searchValue.value = item.name
}

const showResultsOnFocus = () => {
  tempCurrentValue.value = props.value
  searchValue.value = ''
  search(tempCurrentValue.value)
  isOpen.value = true
}

let latestRequestId = 0

const search = value => {
  const requestId = ++latestRequestId

  if (value.length > 0) {
    isLoading.value = true

    PropertyDetailsService.fetchCities({ locale: props.locale, query: value })
      .then(res => {
        if (requestId !== latestRequestId) return

        const responseData = res.data?.data?.getSearchSuggestions?.unifiedSearchSuggestions ?? []
        suggestedOptions.value = responseData.map((city, index) => ({
          id: `${city.concept.translatedName.value}_${index}`,
          name: city.concept.translatedName.value,
          label: `${city.concept.translatedName.value} (${city.concept.locationLabel})`,
        }))

        isLoading.value = false
      })
      .catch(() => {
        if (requestId === latestRequestId) {
          suggestedOptions.value = []
          isLoading.value = false
        }
      })
  } else {
    suggestedOptions.value = []
    isLoading.value = false
  }
}

const triggerUpdate = event => {
  searchValue.value = event.target.value
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => search(event.target.value), 250)
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  clearTimeout(searchTimeout)
})
</script>
